import {
  type CreateDepartmentRequest,
  type DepartmentDetail,
  DepartmentDetailSchema,
  type DepartmentListQuery,
  type DepartmentListResponse,
  DepartmentListResponseSchema,
  type UpdateDepartmentRequest,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Department master API. Every response is validated with the shared Zod
 * schema so a server change can't sneak past the UI types.
 */

export const listDepartments = async (
  query: DepartmentListQuery,
): Promise<DepartmentListResponse> => {
  const { data } = await apiClient.get<unknown>('/departments', { params: query });
  return DepartmentListResponseSchema.parse(data);
};

export const getDepartment = async (id: string): Promise<DepartmentDetail> => {
  const { data } = await apiClient.get<unknown>(`/departments/${id}`);
  return DepartmentDetailSchema.parse(data);
};

export const createDepartment = async (
  payload: CreateDepartmentRequest,
): Promise<DepartmentDetail> => {
  const { data } = await apiClient.post<unknown>('/departments', payload);
  return DepartmentDetailSchema.parse(data);
};

export const updateDepartment = async (
  id: string,
  payload: UpdateDepartmentRequest,
): Promise<DepartmentDetail> => {
  const { data } = await apiClient.patch<unknown>(`/departments/${id}`, payload);
  return DepartmentDetailSchema.parse(data);
};

export const deleteDepartment = async (id: string): Promise<void> => {
  await apiClient.delete(`/departments/${id}`);
};

export const bulkDeleteDepartments = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/departments/bulk-delete', { ids });
  return data;
};

export const bulkSetDepartmentsStatus = async (
  ids: string[],
  isActive: boolean,
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/departments/bulk-set-status', { ids, isActive });
  return data;
};
